import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Flex,
  Heading,
  Spacer,
  Text,
  Td,
  Tr,
} from "@chakra-ui/react";
import CustomButton from "../../components/ButtonComp";
import Loader from "../../components/Loader";
import TableView from "../../components/TableView";
import CustomSingleInput from "../../components/CustomSingleInput";
import { get_bank_reports } from "../../redux/bankReportSlice";
import { get_bank_list } from "../../redux/bankSlice";
import GasCylinderLoader from "../../components/GasCylinderLoader";

const BankReports = () => {
  // Redux selectors
  const { bank_list } = useSelector((state) => state.banks);
  const { bank_reports, loading, total_count } = useSelector(
    (state) => state.bankReports
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [initialValue, setInitialValue] = useState(1);
  const [rows, setRows] = useState(10);
  const [page, setPage] = useState(1);
  const [selectedBank, setSelectedBank] = useState("");

  // 🔥 Flag to detect if user already performed first manual search
  const [hasSearched, setHasSearched] = useState(false);

  // Date filter states
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    startDate: "",
    endDate: "",
    selectedBank: "",
  });

  // For Excel download
  const [excelUrl, setExcelUrl] = useState(null);

  // Load bank list on component mount
  useEffect(() => {
    dispatch(get_bank_list({ page: 1, rows: 100 }));
  }, [dispatch]);

  const get_bank_report_list = async () => {
    const params = {
      page: page,
      limit: rows,
      action: "list",
    };

    if (appliedFilters.startDate) params.fd = appliedFilters.startDate;
    if (appliedFilters.endDate) params.td = appliedFilters.endDate;
    if (appliedFilters.selectedBank) params.bank_id = appliedFilters.selectedBank;

    const response = await dispatch(get_bank_reports(params));

    // Capture excel_url when available
    if (response?.payload?.excel_url) {
      setExcelUrl(response.payload.excel_url);
    } else {
      setExcelUrl(null);
    }
  };

  // Fetch data when filters, rows, or page changes
  useEffect(() => {
    get_bank_report_list();
    setInitialValue((page - 1) * rows + 1);
  }, [page, rows, appliedFilters]);

  // Handle First-Time Search
  const handleSearch = () => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      alert("End date cannot be before start date");
      return;
    }
    setAppliedFilters({ startDate, endDate, selectedBank });
    setPage(1);
    setHasSearched(true); // 🔥 Future searches now become automatic
  };

  // Handle Excel download
  const handleDownloadExcel = () => {
    if (excelUrl) {
      window.open(excelUrl, "_blank");
    } else {
      alert("Excel file not available yet. Please search first.");
    }
  };

  // 🔥 When bank changes → auto search if previously searched
  const handleBankChange = (e) => {
    const value = e.target.value;
    setSelectedBank(value);

    // Auto search logic
    if (hasSearched || (Array.isArray(bank_reports) && bank_reports.length > 0)) {
      setAppliedFilters(prev => ({
        startDate: prev.startDate || startDate,
        endDate: prev.endDate || endDate,
        selectedBank: value,
      }));
      setPage(1);
    }
  };

  const handleRowsChange = (e) => {
    const newRows = Number(e.target.value);
    setRows(newRows);
    setPage(1);
  };

  const handlePrevious = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNext = () => {
    if (page < Math.ceil(total_count / rows)) setPage(page + 1);
  };

  const getStartingIndex = () => {
    return (page - 1) * rows + 1;
  };

  const bankOptions =
    bank_list?.map((bank) => ({
      label: `${bank.bankname} - ${bank.ac_type} - ${bank.accountno}`,
      value: bank.id,
    })) || [];

  const totalPages = Math.max(Math.ceil(total_count / rows), 1);
  const startRecord = total_count > 0 ? (page - 1) * rows + 1 : 0;
  const endRecord = total_count > 0 ? Math.min(page * rows, total_count) : 0;

  return (
    <>
      <Card bg="#fff" paddingInline={5} paddingBlock={2} flexDirection="row" alignItems="center">
        <Heading fontSize="xl">Bank Reports</Heading>
        <Spacer />
      </Card>

      {/* Filters */}
      <Card bg="#fff" py={3} px={5} gap={3}>
        <Flex gap={3} flexWrap="wrap" justify="flex-start">
          <CustomSingleInput width="200px" label="Start Date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          <CustomSingleInput width="200px" label="End Date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          <CustomSingleInput label="Bank" width="200px" placeholder="Select Bank" select options={bankOptions} value={selectedBank} onChange={handleBankChange} />
          <Flex gap={2}>
            <CustomButton title="Search" onClick={handleSearch} />
            {excelUrl && <CustomButton title="Download Excel" variant="outline" onClick={handleDownloadExcel} />}
          </Flex>
        </Flex>
      </Card>

      {loading ? (
        <GasCylinderLoader />
      ) : bank_reports?.length === 0 ? (
        <Card justifyContent="center" alignItems="center" h={20}>
          <Text>No Data Found</Text>
        </Card>
      ) : (
        <Card>
          <TableView
            headData={[
              "S.No.", "Order No.", "Voucher No.", "Voucher Date", "Amount", "Payment Mode",
              "Transaction No", "Transaction Mode", "Cash received", "Online received",
              "Credit", "Debit", "Amount", "Closing Balance",
            ]}
            body={bank_reports?.map((el, i) => (
              <Tr key={`${el.id || i}-${page}`}>
                <Td textAlign="center">{getStartingIndex() + i}</Td>
                <Td textAlign="center">{el.orderids || "NA"}</Td>
                <Td textAlign="center">{el.sr_no || "NA"}</Td>
                <Td textAlign="center">{el.voucher_date || "NA"}</Td>
                <Td textAlign="center">₹{el.amount || "0.00"}</Td>
                <Td textAlign="center">{el.payment_mode || "NA"}</Td>
                <Td textAlign="center">{el.transaction_number || "NA"}</Td>
                <Td textAlign="center">{el.mode_of_transaction || "NA"}</Td>
                <Td textAlign="center">₹{el.cash_received || "0.00"}</Td>
                <Td textAlign="center">₹{el.online_received || "0.00"}</Td>
                <Td textAlign="center">₹{el.online_received || "0.00"}</Td>
                <Td textAlign="center">₹{el.debit || "0.00"}</Td>
                <Td textAlign="center">₹{el.amount || "0.00"}</Td>
                <Td textAlign="center">₹{el.closing_balance || "0.00"}</Td>
              </Tr>
            ))}
            initialValue={initialValue}
            page={page}
            rows={rows}
            total_value={total_count}
            totalPages={totalPages}
            pagination={true}
            onChange={handleRowsChange}
            onPrevious={handlePrevious}
            onNext={handleNext}
          />
        </Card>
      )}
    </>
  );
};

export default BankReports;
